import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Polygon, Marker, DrawingManager, InfoWindow } from '@react-google-maps/api';
import axios from 'axios';

// Google Maps libraries needed
const libraries = ['drawing', 'places'];

// Map container style
const mapContainerStyle = {
    width: '100%',
    height: '500px',
    borderRadius: '8px',
};

// Default map center (UAE)
const defaultCenter = {
    lat: 25.2048,
    lng: 55.2708,
};

// Polygon options
const polygonOptions = {
    fillColor: '#10b981',
    fillOpacity: 0.35,
    strokeColor: '#059669',
    strokeOpacity: 0.9,
    strokeWeight: 2,
    clickable: true,
    editable: true,
    draggable: false,
    zIndex: 1,
};

// Country options for dropdown
const countryOptions = [
    { value: 'UAE', label: 'United Arab Emirates' },
    { value: 'SA', label: 'Saudi Arabia' },
    { value: 'BH', label: 'Bahrain' },
    { value: 'KW', label: 'Kuwait' },
    { value: 'OM', label: 'Oman' },
    { value: 'QA', label: 'Qatar' },
];

export default function ZoneForm() {
    const API_URL = process.env.REACT_APP_API_URL;
    const navigate = useNavigate();
    
    // Extract ID from URL path since we use wildcard routing
    // URL format: /orders/zones/edit/:id
    const path = window.location.pathname;
    const pathParts = path.split('/');
    const isEditMode = path.includes('/zones/edit/');
    const id = isEditMode ? pathParts[pathParts.length - 1] : null;
    
    // Form state
    const [formData, setFormData] = useState({
        zoneCode: '',
        zoneName: '',
        description: '',
        country: '',
        region: '',
        city: '',
        postalCodePrefix: '',
        isActive: true,
    });
    
    // Store original zone code for edit mode validation
    const [originalZoneCode, setOriginalZoneCode] = useState('');
    
    // Show/hide advanced settings
    const [showAdvanced, setShowAdvanced] = useState(false);
    
    // Coordinates state
    const [coordinates, setCoordinates] = useState([]);
    const [mapCenter, setMapCenter] = useState(defaultCenter);
    const [coordinateWarnings, setCoordinateWarnings] = useState([]);
    
    // UI state
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [jsonInput, setJsonInput] = useState('');
    const [drawingMode, setDrawingMode] = useState(null);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [zoneCodeAvailable, setZoneCodeAvailable] = useState(null); // null, true, false
    const [checkingCode, setCheckingCode] = useState(false);
    
    // Refs
    const mapRef = useRef(null);
    const debounceTimer = useRef(null);

    // Load Google Maps
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
        libraries,
    });

    // Fetch zone data for edit mode
    useEffect(() => {
        if (isEditMode && id) {
            fetchZoneData();
        }
    }, [isEditMode, id]);

    const fetchZoneData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}shypvdriverapp/api/pricing/zones/get?id=${id}`);
            
            if (response.data.success && response.data.data) {
                const zone = response.data.data;
                const normalizedCoordinates = Array.isArray(zone.coordinates)
                    ? zone.coordinates.map((coord, index) => ({
                        latitude: Number(coord.latitude ?? coord.lat ?? 0),
                        longitude: Number(coord.longitude ?? coord.lng ?? 0),
                        sequenceOrder: coord.sequenceOrder ?? index + 1,
                        pointType: coord.pointType || 'BOUNDARY',
                        description: coord.description || '',
                        isActive: coord.isActive !== undefined ? coord.isActive : true,
                    }))
                    : [];

                // Store original zone code for validation
                setOriginalZoneCode(zone.zoneCode || '');
                
                setFormData({
                    zoneCode: zone.zoneCode || '',
                    zoneName: zone.zoneName || '',
                    description: zone.description || '',
                    country: zone.country || '',
                    region: zone.region || '',
                    city: zone.city || '',
                    postalCodePrefix: zone.postalCodePrefix || '',
                    isActive: zone.isActive !== undefined ? zone.isActive : true,
                });
                
                if (normalizedCoordinates.length > 0) {
                    setCoordinates(normalizedCoordinates);
                    setShowAdvanced(true);
                    setMapCenter({
                        lat: normalizedCoordinates[0].latitude,
                        lng: normalizedCoordinates[0].longitude,
                    });
                }
                setZoneCodeAvailable(true);
            }
        } catch (err) {
            console.error('Error fetching zone:', err);
            alert('Error loading zone data');
        } finally {
            setLoading(false);
        }
    };
    
    // Calculate distance between two coordinates (Haversine formula)
    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = 
            Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return R * c;
    };
    
    // Check if coordinates are too far from zone center
    const checkCoordinateDistance = useCallback(() => {
        if (coordinates.length === 0) return;
        
        const warnings = [];
        const centerCoord = coordinates.find(c => c.pointType === 'CENTER') || coordinates[0];
        
        coordinates.forEach((coord, index) => {
            if (coord.pointType !== 'CENTER') {
                const distance = calculateDistance(
                    centerCoord.latitude,
                    centerCoord.longitude,
                    coord.latitude,
                    coord.longitude
                );
                
                if (distance > 50) {
                    warnings.push({
                        index,
                        distance: distance.toFixed(2),
                        message: `Point ${index + 1} is ${distance.toFixed(2)}km from center`
                    });
                }
            }
        });
        
        setCoordinateWarnings(warnings);
    }, [coordinates]);
    
    useEffect(() => {
        if (showAdvanced && coordinates.length > 0) {
            checkCoordinateDistance();
        }
    }, [coordinates, showAdvanced, checkCoordinateDistance]);

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const normalizedValue = name === 'zoneCode' ? value.toUpperCase() : value;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : normalizedValue,
        }));
        
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }

        // Check zone code availability on change
        if (name === 'zoneCode' && normalizedValue.trim()) {
            checkZoneCodeAvailability(normalizedValue.trim());
        } else if (name === 'zoneCode' && !normalizedValue.trim()) {
            setZoneCodeAvailable(null);
        }
    };

    // Debounced zone code validation
    const checkZoneCodeAvailability = (code) => {
        // Clear previous timer
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        // Don't check in edit mode if using the original zone code
        if (isEditMode && code === originalZoneCode) {
            setZoneCodeAvailable(true);
            setCheckingCode(false);
            return;
        }

        setCheckingCode(true);
        setZoneCodeAvailable(null);

        // Debounce API call by 500ms
        debounceTimer.current = setTimeout(async () => {
            try {
                let endpoint;
                if (isEditMode) {
                    endpoint = `${API_URL}shypvdriverapp/api/pricing/zones/checkCodeExcluding?code=${encodeURIComponent(code)}&excludeId=${id}`;
                } else {
                    endpoint = `${API_URL}shypvdriverapp/api/pricing/zones/checkCode?code=${encodeURIComponent(code)}`;
                }

                const response = await axios.get(endpoint);
                if (response.data?.success) {
                    setZoneCodeAvailable(response.data?.data === true);
                } else {
                    setZoneCodeAvailable(null);
                }
            } catch (err) {
                console.error('Error checking zone code:', err);
                setZoneCodeAvailable(null);
            } finally {
                setCheckingCode(false);
            }
        }, 400);
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.zoneCode.trim()) {
            newErrors.zoneCode = 'Zone code is required';
        } else if (!/^[A-Za-z0-9-]+$/.test(formData.zoneCode)) {
            newErrors.zoneCode = 'Zone code must be alphanumeric with dashes only';
        } else if (!isEditMode && zoneCodeAvailable === false) {
            newErrors.zoneCode = 'This zone code is already taken';
        } else if (!isEditMode && zoneCodeAvailable !== true) {
            newErrors.zoneCode = 'Please validate the zone code';
        }
        
        if (!formData.zoneName.trim()) {
            newErrors.zoneName = 'Zone name is required';
        }
        
        if (!formData.country) {
            newErrors.country = 'Country is required';
        }
        
        if (!formData.city.trim()) {
            newErrors.city = 'City is required';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setSaving(true);
        
        try {
            const payload = {
                ...formData,
                coordinates: showAdvanced ? coordinates.map((coord, index) => ({
                    latitude: coord.latitude || coord.lat,
                    longitude: coord.longitude || coord.lng,
                    sequenceOrder: index + 1,
                    pointType: coord.pointType || 'BOUNDARY',
                })) : [],
            };
            
            let response;
            if (isEditMode) {
                payload.zoneId = Number(id);
                response = await axios.post(`${API_URL}shypvdriverapp/api/pricing/zones/update`, payload);
            } else {
                response = await axios.post(`${API_URL}shypvdriverapp/api/pricing/zones/create`, payload);
            }
            
            if (response.data.success) {
                alert(`Zone ${isEditMode ? 'updated' : 'created'} successfully!`);
                navigate('/orders/zones');
            } else {
                alert(response.data.message || 'Failed to save zone');
            }
        } catch (err) {
            console.error('Error saving zone:', err);
            if (err.response?.data?.message) {
                alert(err.response.data.message);
            } else {
                alert('Error saving zone. Please try again.');
            }
        } finally {
            setSaving(false);
        }
    };

    // Map callback
    const onMapLoad = useCallback((map) => {
        mapRef.current = map;
    }, []);

    // Handle polygon complete from drawing
    const onPolygonComplete = useCallback((poly) => {
        const path = poly.getPath();
        const newCoordinates = [];
        
        for (let i = 0; i < path.getLength(); i++) {
            const point = path.getAt(i);
            newCoordinates.push({
                latitude: point.lat(),
                longitude: point.lng(),
                sequenceOrder: i + 1,
                pointType: 'BOUNDARY',
            });
        }
        
        setCoordinates(newCoordinates);
        poly.setMap(null);
        setDrawingMode(null);
    }, []);

    // Handle map click to add marker
    const handleMapClick = useCallback((e) => {
        if (drawingMode === 'marker') {
            const newCoord = {
                latitude: e.latLng.lat(),
                longitude: e.latLng.lng(),
                sequenceOrder: coordinates.length + 1,
                pointType: 'BOUNDARY',
            };
            setCoordinates(prev => [...prev, newCoord]);
        }
    }, [drawingMode, coordinates.length]);

    // Update coordinate
    const updateCoordinate = (index, field, value) => {
        setCoordinates(prev => prev.map((coord, i) => {
            if (i === index) {
                return { ...coord, [field]: parseFloat(value) || 0 };
            }
            return coord;
        }));
    };

    // Handle marker drag
    const handleMarkerDrag = (index, e) => {
        const newLat = e.latLng.lat();
        const newLng = e.latLng.lng();
        setCoordinates(prev => prev.map((coord, i) => {
            if (i === index) {
                return { ...coord, latitude: newLat, longitude: newLng };
            }
            return coord;
        }));
    };

    // Handle marker click
    const handleMarkerClick = (index) => {
        setSelectedMarker(selectedMarker === index ? null : index);
    };

    // Remove coordinate
    const removeCoordinate = (index) => {
        setCoordinates(prev => {
            const updated = prev.filter((_, i) => i !== index);
            return updated.map((coord, i) => ({
                ...coord,
                sequenceOrder: i + 1,
            }));
        });
    };

    // Clear all coordinates
    const clearAllCoordinates = () => {
        if (window.confirm('Are you sure you want to clear all coordinates?')) {
            setCoordinates([]);
        }
    };

    // Import coordinates from JSON
    const handleImportJson = () => {
        try {
            const parsed = JSON.parse(jsonInput);
            
            let importedCoords = [];
            
            if (Array.isArray(parsed)) {
                importedCoords = parsed.map((item, index) => ({
                    latitude: item.latitude || item.lat || item[0],
                    longitude: item.longitude || item.lng || item[1],
                    sequenceOrder: index + 1,
                    pointType: item.pointType || 'BOUNDARY',
                }));
            } else if (parsed.coordinates) {
                importedCoords = parsed.coordinates.map((item, index) => ({
                    latitude: item.latitude || item.lat || item[0],
                    longitude: item.longitude || item.lng || item[1],
                    sequenceOrder: index + 1,
                    pointType: item.pointType || 'BOUNDARY',
                }));
            }
            
            if (importedCoords.length > 0) {
                setCoordinates(importedCoords);
                setMapCenter({
                    lat: importedCoords[0].latitude,
                    lng: importedCoords[0].longitude,
                });
                setImportModalOpen(false);
                setJsonInput('');
                alert(`Successfully imported ${importedCoords.length} coordinates`);
            } else {
                alert('No valid coordinates found in JSON');
            }
        } catch (err) {
            alert('Invalid JSON format. Please check your input.');
        }
    };

    // Export coordinates to JSON
    const exportToJson = () => {
        const json = JSON.stringify(coordinates, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `zone-${formData.zoneCode || 'coordinates'}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // Convert coordinates for polygon display
    const polygonPaths = coordinates.map(coord => ({
        lat: coord.latitude ?? coord.lat,
        lng: coord.longitude ?? coord.lng,
    }));

    const styles = {
        container: {
            padding: '24px 40px',
            backgroundColor: '#f9fafb',
            minHeight: '100vh',
        },
        header: {
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
        },
        backButton: {
            padding: '10px 16px',
            backgroundColor: '#fff',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
        },
        card: {
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            marginBottom: '20px',
            transition: 'all 0.3s ease',
        },
        advancedCard: {
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            marginBottom: '20px',
            animation: 'slideDown 0.4s ease-out',
            transformOrigin: 'top',
        },
        sectionTitle: {
            fontSize: '16px',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '20px',
            paddingBottom: '12px',
            borderBottom: '2px solid #e5e7eb',
            textAlign: 'left',
        },
        formRow: {
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '24px',
            marginBottom: '20px',
        },
        formGroup: {
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            position: 'relative',
        },
        label: {
            fontSize: '13px',
            fontWeight: '500',
            color: '#1f2937',
            marginBottom: '0px',
            textAlign: 'left',
        },
        required: {
            color: '#dc2626',
            marginLeft: '4px',
        },
        input: {
            padding: '12px 16px',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            backgroundColor: '#f3f4f6',
            transition: 'all 0.2s ease',
            outline: 'none',
            width: '100%',
        },
        inputError: {
            backgroundColor: '#fee2e2',
            border: '1px solid #dc2626',
        },
        errorText: {
            fontSize: '12px',
            color: '#dc2626',
        },
        toggleButton: {
            padding: '12px 24px',
            backgroundColor: '#4f46e5',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 4px rgba(79, 70, 229, 0.2)',
        },
        toggleButtonHover: {
            backgroundColor: '#4338ca',
            boxShadow: '0 4px 6px rgba(79, 70, 229, 0.3)',
        },
        toolButton: {
            padding: '8px 16px',
            backgroundColor: '#fff',
            color: '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
        },
        toolButtonActive: {
            backgroundColor: '#4f46e5',
            color: '#fff',
            borderColor: '#4f46e5',
        },
        buttonGroup: {
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            marginTop: '24px',
        },
        btnPrimary: {
            padding: '10px 24px',
            backgroundColor: '#4f46e5',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
        },
        btnSecondary: {
            padding: '10px 24px',
            backgroundColor: '#fff',
            color: '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
        },
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                    <div style={{ fontSize: '16px', color: '#6b7280' }}>Loading zone data...</div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <style>
                {`
                    @keyframes slideDown {
                        from {
                            opacity: 0;
                            transform: translateY(-20px);
                            max-height: 0;
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                            max-height: 5000px;
                        }
                    }
                    @keyframes fadeIn {
                        from {
                            opacity: 0;
                        }
                        to {
                            opacity: 1;
                        }
                    }
                    input:focus, select:focus, textarea:focus {
                        background-color: #ffffff !important;
                        box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1) !important;
                    }
                `}
            </style>
            {/* Header */}
            <div style={styles.header}>
                <button style={styles.backButton} onClick={() => navigate('/orders/zones')}>
                    ← Back
                </button>
                <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#111827' }}>
                    {isEditMode ? 'Edit Zone' : 'Create New Zone'}
                </h1>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Basic Information Card */}
                <div style={styles.card}>
                    <div style={styles.sectionTitle}>Basic Information</div>
                    
                    <div style={styles.formRow}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                Zone Code<span style={styles.required}>*</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    name="zoneCode"
                                    value={formData.zoneCode}
                                    onChange={handleInputChange}
                                    placeholder="e.g., ZONE-DXB-001"
                                    style={{ 
                                        ...styles.input, 
                                        ...(errors.zoneCode ? styles.inputError : {}),
                                        paddingRight: '40px'
                                    }}
                                    disabled={isEditMode}
                                />
                                {!isEditMode && formData.zoneCode && (
                                    <div style={{
                                        position: 'absolute',
                                        right: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        fontSize: '18px',
                                    }}>
                                        {checkingCode ? (
                                            <span style={{ color: '#6b7280', fontSize: '12px' }}>...</span>
                                        ) : zoneCodeAvailable === true ? (
                                            <span style={{ color: '#10b981' }}>✔</span>
                                        ) : zoneCodeAvailable === false ? (
                                            <span style={{ color: '#dc2626' }}>✖</span>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                            {errors.zoneCode && <span style={styles.errorText}>{errors.zoneCode}</span>}
                            {!errors.zoneCode && zoneCodeAvailable === true && !isEditMode && (
                                <span style={{ fontSize: '12px', color: '#10b981' }}>Zone code is available</span>
                            )}
                            {!errors.zoneCode && zoneCodeAvailable === false && !isEditMode && (
                                <span style={{ fontSize: '12px', color: '#dc2626' }}>Zone code is already taken</span>
                            )}
                            {!errors.zoneCode && zoneCodeAvailable === null && !isEditMode && !checkingCode && formData.zoneCode.trim() && (
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>Unable to verify code. Try again.</span>
                            )}
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                Zone Name<span style={styles.required}>*</span>
                            </label>
                            <input
                                type="text"
                                name="zoneName"
                                value={formData.zoneName}
                                onChange={handleInputChange}
                                placeholder="e.g., Dubai Downtown"
                                style={{ ...styles.input, ...(errors.zoneName ? styles.inputError : {}) }}
                            />
                            {errors.zoneName && <span style={styles.errorText}>{errors.zoneName}</span>}
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                Country<span style={styles.required}>*</span>
                            </label>
                            <select
                                name="country"
                                value={formData.country}
                                onChange={handleInputChange}
                                style={{ ...styles.input, ...(errors.country ? styles.inputError : {}) }}
                            >
                                <option value="">Select Country</option>
                                {countryOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            {errors.country && <span style={styles.errorText}>{errors.country}</span>}
                        </div>
                    </div>

                    <div style={styles.formRow}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                City<span style={styles.required}>*</span>
                            </label>
                            <input
                                type="text"
                                name="city"
                                value={formData.city}
                                onChange={handleInputChange}
                                placeholder="e.g., Dubai"
                                style={{ ...styles.input, ...(errors.city ? styles.inputError : {}) }}
                            />
                            {errors.city && <span style={styles.errorText}>{errors.city}</span>}
                        </div>
                    </div>

                    <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                name="isActive"
                                checked={formData.isActive}
                                onChange={handleInputChange}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Is Active</span>
                        </label>
                    </div>
                </div>

                {/* Advanced Settings Toggle */}
                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                    <button
                        type="button"
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        style={styles.toggleButton}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#4338ca';
                            e.currentTarget.style.boxShadow = '0 4px 6px rgba(79, 70, 229, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#4f46e5';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(79, 70, 229, 0.2)';
                        }}
                    >
                        <span style={{ transition: 'transform 0.3s ease', display: 'inline-block', transform: showAdvanced ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                        {showAdvanced ? 'Hide' : 'Show'} Advanced Settings
                    </button>
                </div>

                {/* Advanced Settings Card */}
                {showAdvanced && (
                    <div style={styles.advancedCard}>
                        <div style={styles.sectionTitle}>Optional Information</div>
                        
                        <div style={styles.formRow}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Region / State</label>
                                <input
                                    type="text"
                                    name="region"
                                    value={formData.region}
                                    onChange={handleInputChange}
                                    placeholder="e.g., Dubai"
                                    style={styles.input}
                                />
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>Postal Code Prefix</label>
                                <input
                                    type="text"
                                    name="postalCodePrefix"
                                    value={formData.postalCodePrefix}
                                    onChange={handleInputChange}
                                    placeholder="e.g., 00000"
                                    style={styles.input}
                                />
                            </div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Description</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder="Enter zone description..."
                                rows="3"
                                style={{ ...styles.input, resize: 'vertical' }}
                            />
                        </div>

                        <div style={{ ...styles.sectionTitle, marginTop: '32px' }}>Zone Boundaries (Coordinates)</div>

                        {/* Map Controls */}
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                            <button
                                type="button"
                                onClick={() => setDrawingMode(drawingMode === 'polygon' ? null : 'polygon')}
                                style={{
                                    ...styles.toolButton,
                                    ...(drawingMode === 'polygon' ? styles.toolButtonActive : {})
                                }}
                            >
                                Draw Polygon
                            </button>
                            <button
                                type="button"
                                onClick={() => setDrawingMode(drawingMode === 'marker' ? null : 'marker')}
                                style={{
                                    ...styles.toolButton,
                                    ...(drawingMode === 'marker' ? styles.toolButtonActive : {})
                                }}
                            >
                                Add Points
                            </button>
                            <button
                                type="button"
                                onClick={clearAllCoordinates}
                                disabled={coordinates.length === 0}
                                style={{
                                    ...styles.toolButton,
                                    color: '#dc2626',
                                    cursor: coordinates.length === 0 ? 'not-allowed' : 'pointer',
                                    opacity: coordinates.length === 0 ? 0.5 : 1,
                                }}
                            >
                                Clear All
                            </button>
                            <button
                                type="button"
                                onClick={() => setImportModalOpen(true)}
                                style={styles.toolButton}
                            >
                                Import JSON
                            </button>
                            <button
                                type="button"
                                onClick={exportToJson}
                                disabled={coordinates.length === 0}
                                style={{
                                    ...styles.toolButton,
                                    cursor: coordinates.length === 0 ? 'not-allowed' : 'pointer',
                                    opacity: coordinates.length === 0 ? 0.5 : 1,
                                }}
                            >
                                Export JSON
                            </button>
                        </div>

                        {/* Google Map */}
                        <div style={{ marginBottom: '20px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                            {loadError && (
                                <div style={{ padding: '40px', textAlign: 'center', color: '#dc2626' }}>
                                    Error loading Google Maps
                                </div>
                            )}
                            
                            {!isLoaded ? (
                                <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                                    Loading map...
                                </div>
                            ) : (
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    center={mapCenter}
                                    zoom={12}
                                    onLoad={onMapLoad}
                                    onClick={handleMapClick}
                                    options={{
                                        streetViewControl: false,
                                        mapTypeControl: true,
                                        fullscreenControl: true,
                                    }}
                                >
                                    {drawingMode === 'polygon' && (
                                        <DrawingManager
                                            onPolygonComplete={onPolygonComplete}
                                            drawingMode="polygon"
                                            options={{
                                                drawingControl: false,
                                                polygonOptions: polygonOptions,
                                            }}
                                        />
                                    )}

                                    {coordinates.length > 2 && (
                                        <Polygon
                                            paths={polygonPaths}
                                            options={polygonOptions}
                                        />
                                    )}

                                    {coordinates.map((coord, index) => (
                                        <Marker
                                            key={index}
                                            position={{
                                                lat: coord.latitude,
                                                lng: coord.longitude,
                                            }}
                                            label={{
                                                text: String(index + 1),
                                                color: 'white',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                            }}
                                            title={`Point ${index + 1}`}
                                            draggable={true}
                                            onDragEnd={(e) => handleMarkerDrag(index, e)}
                                            onClick={() => handleMarkerClick(index)}
                                        >
                                            {selectedMarker === index && (
                                                <InfoWindow
                                                    onCloseClick={() => setSelectedMarker(null)}
                                                    position={{
                                                        lat: coord.latitude,
                                                        lng: coord.longitude,
                                                    }}
                                                >
                                                    <div style={{ padding: '8px', minWidth: '200px' }}>
                                                        <h3 style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                                            Point {index + 1}
                                                        </h3>
                                                        <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.6' }}>
                                                            <div style={{ marginBottom: '4px' }}>
                                                                <strong>Latitude:</strong> {coord.latitude.toFixed(6)}
                                                            </div>
                                                            <div style={{ marginBottom: '4px' }}>
                                                                <strong>Longitude:</strong> {coord.longitude.toFixed(6)}
                                                            </div>
                                                            <div style={{ marginBottom: '4px' }}>
                                                                <strong>Type:</strong> {coord.pointType || 'BOUNDARY'}
                                                            </div>
                                                            <div style={{ marginTop: '8px', padding: '6px 8px', backgroundColor: '#f3f4f6', borderRadius: '4px', fontSize: '11px', color: '#4b5563' }}>
                                                                Drag marker to reposition
                                                            </div>
                                                        </div>
                                                    </div>
                                                </InfoWindow>
                                            )}
                                        </Marker>
                                    ))}
                                </GoogleMap>
                            )}
                        </div>

                        {/* Coordinate Warnings */}
                        {coordinateWarnings.length > 0 && (
                            <div style={{
                                padding: '12px 16px',
                                backgroundColor: '#fef3c7',
                                borderRadius: '8px',
                                marginBottom: '16px',
                                border: '1px solid #fbbf24',
                                animation: 'fadeIn 0.3s ease'
                            }}>
                                <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '8px', fontSize: '14px' }}>
                                    Distance Warnings
                                </div>
                                {coordinateWarnings.map((warning, idx) => (
                                    <div key={idx} style={{ fontSize: '13px', color: '#92400e', marginBottom: '4px' }}>
                                        • {warning.message} (Too far from zone center)
                                    </div>
                                ))}
                                <p style={{ fontSize: '12px', color: '#92400e', margin: '8px 0 0', fontStyle: 'italic' }}>
                                    These points are far from the zone center but you can still save them if needed.
                                </p>
                            </div>
                        )}

                        {/* Coordinates Table */}
                        {coordinates.length === 0 ? (
                            <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                                <div style={{ fontSize: '16px', color: '#6b7280', marginBottom: '8px' }}>
                                    No coordinates added yet
                                </div>
                                <div style={{ fontSize: '14px', color: '#9ca3af' }}>
                                    Use the drawing tools above or import from JSON
                                </div>
                            </div>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f9fafb' }}>
                                            <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>#</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Latitude</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Longitude</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Type</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Status</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {coordinates.map((coord, index) => {
                                            const warning = coordinateWarnings.find(w => w.index === index);
                                            return (
                                                <tr key={index} style={{ backgroundColor: warning ? '#fffbeb' : 'transparent' }}>
                                                    <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', fontSize: '14px' }}>{index + 1}</td>
                                                    <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                                                        <input
                                                            type="number"
                                                            step="0.000001"
                                                            value={coord.latitude}
                                                            onChange={(e) => updateCoordinate(index, 'latitude', e.target.value)}
                                                            style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
                                                        />
                                                    </td>
                                                    <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                                                        <input
                                                            type="number"
                                                            step="0.000001"
                                                            value={coord.longitude}
                                                            onChange={(e) => updateCoordinate(index, 'longitude', e.target.value)}
                                                            style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
                                                        />
                                                    </td>
                                                    <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                                                        <select
                                                            value={coord.pointType || 'BOUNDARY'}
                                                            onChange={(e) => updateCoordinate(index, 'pointType', e.target.value)}
                                                            style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
                                                        >
                                                            <option value="BOUNDARY">Boundary</option>
                                                            <option value="CENTER">Center</option>
                                                            <option value="MARKER">Marker</option>
                                                        </select>
                                                    </td>
                                                    <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                                                        {warning ? (
                                                            <span style={{ color: '#92400e', fontSize: '11px', fontWeight: '500' }}>
                                                                Too far ({warning.distance}km)
                                                            </span>
                                                        ) : (
                                                            <span style={{ color: '#059669', fontSize: '11px', fontWeight: '500' }}>
                                                                OK
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeCoordinate(index)}
                                                            style={{
                                                                padding: '6px 12px',
                                                                backgroundColor: '#fee2e2',
                                                                color: '#dc2626',
                                                                border: '1px solid #fca5a5',
                                                                borderRadius: '4px',
                                                                fontSize: '12px',
                                                                cursor: 'pointer',
                                                                fontWeight: '500',
                                                                transition: 'all 0.2s ease',
                                                            }}
                                                        >
                                                            Remove
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Form Actions */}
                <div style={styles.buttonGroup}>
                    <button type="button" onClick={() => navigate('/orders/zones')} style={styles.btnSecondary}>
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving || checkingCode || (!isEditMode && zoneCodeAvailable !== true)}
                        style={{
                            ...styles.btnPrimary,
                            opacity: (saving || checkingCode || (!isEditMode && zoneCodeAvailable !== true)) ? 0.6 : 1,
                            cursor: (saving || checkingCode || (!isEditMode && zoneCodeAvailable !== true)) ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {saving ? 'Saving...' : isEditMode ? 'Update Zone' : 'Create Zone'}
                    </button>
                </div>
            </form>

            {/* Import JSON Modal */}
            {importModalOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                }}>
                    <div style={{
                        backgroundColor: '#fff',
                        borderRadius: '12px',
                        width: '90%',
                        maxWidth: '600px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                    }}>
                        <div style={{
                            padding: '20px 24px',
                            borderBottom: '1px solid #e5e7eb',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                                Import Coordinates from JSON
                            </h3>
                            <button
                                onClick={() => setImportModalOpen(false)}
                                style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    fontSize: '20px',
                                    color: '#6b7280',
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div style={{ padding: '24px' }}>
                            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
                                Paste your JSON coordinates below. Supported formats:
                            </p>
                            <ul style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px', paddingLeft: '20px' }}>
                                <li><code>[{`{latitude: 25.2, longitude: 55.2}`}, ...]</code></li>
                                <li><code>[{`{lat: 25.2, lng: 55.2}`}, ...]</code></li>
                                <li><code>[[25.2, 55.2], ...]</code></li>
                            </ul>
                            <textarea
                                value={jsonInput}
                                onChange={(e) => setJsonInput(e.target.value)}
                                placeholder='[{"latitude": 25.2048, "longitude": 55.2708}, ...]'
                                rows="10"
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    fontFamily: 'monospace',
                                    fontSize: '13px',
                                    resize: 'vertical',
                                }}
                            />
                        </div>
                        <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <button
                                onClick={() => setImportModalOpen(false)}
                                style={styles.btnSecondary}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleImportJson}
                                disabled={!jsonInput.trim()}
                                style={{ ...styles.btnPrimary, opacity: !jsonInput.trim() ? 0.5 : 1 }}
                            >
                                Import Coordinates
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
